import { TFunction } from "i18next";

/**
 * A single question and answer in the FAQs.
 */
export interface HelpFaqItem {
    /**
     * Unique key for the item, used to track which answers are open.
     */
    key: string,
    question: string,
    /**
     * Answer to the question. This is trusted HTML from our own translations and may contain simple formatting such as <strong> and lists.
     */
    answerHtml: string,
}

/**
 * A group of related questions in the FAQs.
 */
export interface HelpFaqCategory {
    key: string,
    title: string,
    /**
     * Shorter title used in the category navigation.
     */
    navigationTitle: string,
    /**
     * True if this category is aimed at delegates completing a questionnaire rather than admins running campaigns.
     */
    isForDelegates: boolean,
    items: Array<HelpFaqItem>,
}

/**
 * Returns the categories of questions shown on the FAQs help page.
 */
export function getHelpFaqCategories(t: TFunction): Array<HelpFaqCategory> {
    return [
        {
            key: 'getting-started',
            title: t('helpFaqs.gettingStarted.title', 'Getting started'),
            navigationTitle: t('helpFaqs.gettingStarted.navigationTitle', 'Getting started'),
            isForDelegates: false,
            items: [
                {
                    key: 'what-is-impacts',
                    question: t('helpFaqs.gettingStarted.whatIsImpacts.question', 'What is IMPACTS?'),
                    answerHtml: t('helpFaqs.gettingStarted.whatIsImpacts.answer', 'IMPACTS measures whether training actually works, not just whether people completed it. It tracks pre- and post-training assessments and workplace application, and links results back to cost so you can report a real ROI figure.'),
                },
                {
                    key: 'what-is-a-campaign',
                    question: t('helpFaqs.gettingStarted.whatIsACampaign.question', 'What is a "campaign"?'),
                    answerHtml: t('helpFaqs.gettingStarted.whatIsACampaign.answer', 'A campaign is a single round of measurement: one questionnaire sent to a group of delegates, on a schedule, optionally linked to a specific learning event. You\'ll typically create one campaign per training programme or cohort.'),
                },
                {
                    key: 'how-long-does-setup-take',
                    question: t('helpFaqs.gettingStarted.howLong.question', 'How long does setup take?'),
                    answerHtml: t('helpFaqs.gettingStarted.howLong.answer', 'A few minutes if you\'re using the default questionnaire template. There are five stages: Campaign, Questionnaire, People, Schedule, and Review. You can save and come back to any stage before launching.'),
                },
            ],
        },
        {
            key: 'setup',
            title: t('helpFaqs.setup.title', 'Setting up a campaign'),
            navigationTitle: t('helpFaqs.setup.navigationTitle', 'Setting up a campaign'),
            isForDelegates: false,
            items: [
                {
                    key: 'link-learning-event',
                    question: t('helpFaqs.setup.linkLearningEvent.question', 'Do I have to link a learning event?'),
                    answerHtml: t('helpFaqs.setup.linkLearningEvent.answer', 'No, the toggle on the Campaign stage is optional. But if you want ROI reporting later, you\'ll need to add the event\'s cost fields here, since that\'s the only place cost is captured.'),
                },
                {
                    key: 'unknown-cost',
                    question: t('helpFaqs.setup.unknownCost.question', 'What if I don\'t know the cost yet?'),
                    answerHtml: t('helpFaqs.setup.unknownCost.answer', 'You can leave it blank and add it later by editing the campaign. See <strong>Editing &amp; reopening</strong> for what can and can\'t be changed once a campaign is live.'),
                },
                {
                    key: 'default-template',
                    question: t('helpFaqs.setup.defaultTemplate.question', 'What\'s included in the default questionnaire template?'),
                    answerHtml: t('helpFaqs.setup.defaultTemplate.answer', 'Seven sections: Intent, Make It Happen, Performance, Action, Continuous Learning, Transformation, and Self-Coaching. Each is fully editable. You can rename it, change the description, or edit, add and remove individual questions.'),
                },
                {
                    key: 'own-questionnaire',
                    question: t('helpFaqs.setup.ownQuestionnaire.question', 'Can I build my own questionnaire instead?'),
                    answerHtml: t('helpFaqs.setup.ownQuestionnaire.answer', 'Yes. Start from scratch and add your own sections, or take the default template and edit it until it fits.'),
                },
                {
                    key: 'delegate-information',
                    question: t('helpFaqs.setup.delegateInformation.question', 'What information do I need for each delegate?'),
                    answerHtml: t('helpFaqs.setup.delegateInformation.answer', 'Email, first name and last name are required. Department and line manager name and email are optional but recommended: leaving line manager details blank is the main reason Workplace Application and manager-feedback reporting comes back incomplete.'),
                },
                {
                    key: 'import-formats',
                    question: t('helpFaqs.setup.importFormats.question', 'What file formats work for bulk import?'),
                    answerHtml: t('helpFaqs.setup.importFormats.answer', 'Excel (.xlsx), CSV (.csv), or tab-delimited (.txt/.tsv). For smaller groups you can also add people manually, up to 5 at a time.'),
                },
            ],
        },
        {
            key: 'editing',
            title: t('helpFaqs.editing.title', 'Editing & reopening a campaign'),
            navigationTitle: t('helpFaqs.editing.navigationTitle', 'Editing & reopening'),
            isForDelegates: false,
            items: [
                {
                    key: 'edit-when-live',
                    question: t('helpFaqs.editing.editWhenLive.question', 'Can I edit a campaign once it\'s live?'),
                    answerHtml: t('helpFaqs.editing.editWhenLive.answer', 'Only certain parts. <strong>People</strong> can be edited any time: add or remove delegates, update emails, and resend invites. On <strong>Schedule</strong>, you can change the launch date if it hasn\'t passed yet, change the end date any time before it passes, and switch reminders on or off at any point.'),
                },
                {
                    key: 'edit-details-when-live',
                    question: t('helpFaqs.editing.editDetailsWhenLive.question', 'Can I edit the Campaign details or Questionnaire once live?'),
                    answerHtml: t('helpFaqs.editing.editDetailsWhenLive.answer', 'No. Those two stages are locked once a campaign has launched.'),
                },
                {
                    key: 'reminder-emails',
                    question: t('helpFaqs.editing.reminderEmails.question', 'How do reminder emails work?'),
                    answerHtml: t('helpFaqs.editing.reminderEmails.answer', 'If switched on, reminders go out weekly to anyone who hasn\'t completed, on the same weekday as launch. For example, a Tuesday launch means reminders every Tuesday. In the campaign\'s final week, this switches to daily.'),
                },
                {
                    key: 'reminders-after-launch',
                    question: t('helpFaqs.editing.remindersAfterLaunch.question', 'Can I turn reminders on after launch if I forgot?'),
                    answerHtml: t('helpFaqs.editing.remindersAfterLaunch.answer', 'Yes, and you can turn them off again too. The toggle stays editable throughout.'),
                },
                {
                    key: 'campaign-closes',
                    question: t('helpFaqs.editing.campaignCloses.question', 'What happens when a campaign closes?'),
                    answerHtml: t('helpFaqs.editing.campaignCloses.answer', 'It doesn\'t have to stay closed. You can reopen a closed campaign with new launch and end dates, add new people, and manually resend invites to anyone who didn\'t complete it the first time via <strong>Manage All Participants</strong>. This isn\'t automatic: you choose who to re-invite.'),
                },
            ],
        },
        {
            key: 'results',
            title: t('helpFaqs.results.title', 'Results & reporting'),
            navigationTitle: t('helpFaqs.results.navigationTitle', 'Results & reporting'),
            isForDelegates: false,
            items: [
                {
                    key: 'when-results',
                    question: t('helpFaqs.results.whenResults.question', 'When can I see results?'),
                    answerHtml: t('helpFaqs.results.whenResults.answer', 'As soon as the campaign is live. You don\'t need to wait until it closes. The campaign screen shows real-time Invited vs Completed counts, with drill-down lists of who has and hasn\'t completed.'),
                },
                {
                    key: 'filter-results',
                    question: t('helpFaqs.results.filterResults.question', 'Can I filter or focus results on specific delegates?'),
                    answerHtml: t('helpFaqs.results.filterResults.answer', 'Yes. Use Results Criteria to select specific learners or all of them, and view results grouped by category.'),
                },
                {
                    key: 'categories',
                    question: t('helpFaqs.results.categories.question', 'What are "categories"?'),
                    answerHtml: t('helpFaqs.results.categories.answer', 'Questionnaire sections roll up into business-relevant categories (Cost Savings, Engagement, Performance Improvement, Personal Development, Process Improvement, Profit, and Revenue). Each is shown as a percentage change, with a bar graph view available.'),
                },
                {
                    key: 'export-report',
                    question: t('helpFaqs.results.exportReport.question', 'Can I export a report?'),
                    answerHtml: t('helpFaqs.results.exportReport.answer', 'Yes, at any point, even with just one response in. You can export it as a PDF, print it, or use the graph view.'),
                },
            ],
        },
        {
            key: 'admin',
            title: t('helpFaqs.admin.title', 'Admin, roles & access'),
            navigationTitle: t('helpFaqs.admin.navigationTitle', 'Admin, roles & access'),
            isForDelegates: false,
            items: [
                {
                    key: 'multiple-admins',
                    question: t('helpFaqs.admin.multipleAdmins.question', 'Can more than one person in my organisation have admin access?'),
                    answerHtml: t('helpFaqs.admin.multipleAdmins.answer', 'Yes. Your account can have multiple admins with different permission levels, managed under the <strong>Admin</strong> tab.'),
                },
                {
                    key: 'access-levels',
                    question: t('helpFaqs.admin.accessLevels.question', 'What are the different access levels?'),
                    answerHtml: t('helpFaqs.admin.accessLevels.answer', '<ul><li><strong>Subscription administrator</strong>: full account access.</li><li><strong>Department administrator</strong>: limited to their own department.</li><li><strong>User</strong>: can only see their own survey. This is typically the delegate-level login.</li></ul>'),
                },
                {
                    key: 'add-admin',
                    question: t('helpFaqs.admin.addAdmin.question', 'How do I add another admin?'),
                    answerHtml: t('helpFaqs.admin.addAdmin.answer', 'Under <strong>Admin → People</strong>, click <strong>Add</strong>, fill in their name and email, and choose a Security Group. Note that departments must be set up first, under <strong>Admin → Departments</strong>, before you can assign a person to one.'),
                },
                {
                    key: 'departments',
                    question: t('helpFaqs.admin.departments.question', 'Where do departments come from?'),
                    answerHtml: t('helpFaqs.admin.departments.answer', 'You can add them in two places: under <strong>Admin → Departments</strong>, or directly from the Department dropdown while adding people during campaign setup.'),
                },
            ],
        },
        {
            key: 'data',
            title: t('helpFaqs.data.title', 'Data & privacy'),
            navigationTitle: t('helpFaqs.data.navigationTitle', 'Data & privacy'),
            isForDelegates: false,
            items: [
                {
                    key: 'data-hosting',
                    question: t('helpFaqs.data.dataHosting.question', 'Where is participant data hosted, and is IMPACTS GDPR-compliant?'),
                    answerHtml: t('helpFaqs.data.dataHosting.answer', 'Data is hosted within our secure data centres within the UK and EEA, and where appropriate processed within the UK and EEA. Please contact your account manager if you require a copy of our GDPR policy or Privacy Notice.'),
                },
            ],
        },
        {
            key: 'delegates',
            title: t('helpFaqs.delegates.title', 'For delegates'),
            navigationTitle: t('helpFaqs.delegates.navigationTitle', 'For delegates'),
            isForDelegates: true,
            items: [
                {
                    key: 'find-questionnaire',
                    question: t('helpFaqs.delegates.findQuestionnaire.question', 'Where do I find my questionnaire?'),
                    answerHtml: t('helpFaqs.delegates.findQuestionnaire.answer', 'Log in to your IMPACTS workspace. Your homepage shows how many questionnaires you currently have to complete. Click into a campaign tile marked <strong>Start</strong> to begin.'),
                },
                {
                    key: 'completed-questionnaires',
                    question: t('helpFaqs.delegates.completedQuestionnaires.question', 'Can I see questionnaires I\'ve already completed?'),
                    answerHtml: t('helpFaqs.delegates.completedQuestionnaires.answer', 'Yes. They\'re listed under <strong>Completed</strong> on your homepage, along with the date you completed each one. Click <strong>View</strong> to look back at any of them.'),
                },
                {
                    key: 'multiple-campaigns',
                    question: t('helpFaqs.delegates.multipleCampaigns.question', 'I\'ve been invited to more than one campaign. Where do I find them all?'),
                    answerHtml: t('helpFaqs.delegates.multipleCampaigns.answer', 'All your campaigns, live and completed, appear in the same workspace. You\'ll only ever see your own results, even if you\'re part of several campaigns run by different teams.'),
                },
            ],
        },
    ];
}
